/** @jsxImportSource react */
import { useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Paper, Typography } from '@mui/material'
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { formatValue } from './fieldValues'
import { LookupLink } from './LookupInput'
import { formatRelativeTime } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import Loading from '../common/Loading'
import ApprovalAskDialog, { approvalNeeded, approvalPending } from '../process/ApprovalAskDialog'

const PER_COLUMN = 50
const NONE = '__none__'
const columnId = (value) => `col:${value ?? NONE}`
const toNumber = (v) => (v === undefined || v === null || v === '' ? 0 : Number(v))

const CardBody = ({ record, cardFields, sumField, dragging }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25, cursor: 'grab', bgcolor: 'background.paper', boxShadow: dragging ? 6 : 'none',
      '&:hover': { borderColor: 'primary.main' },
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{record.title}</Typography>
    {sumField && record.data[sumField.fieldKey] !== undefined && (
      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>{formatValue(sumField, record.data[sumField.fieldKey])}</Typography>
    )}
    {cardFields.map((f) => {
      const value = record.data[f.fieldKey]
      if (value === undefined || value === null || value === '') return null
      return (
        <Typography key={f.fieldKey} variant="caption" color="text.secondary" component="div" noWrap>
          {f.label}: {f.fieldType === 'LOOKUP' || f.fieldType === 'USER' ? <LookupLink refValue={record.lookups?.[f.fieldKey]} /> : formatValue(f, value)}
        </Typography>
      )
    })}
    <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 0.5 }}>
      Updated {formatRelativeTime(record.updatedOn)}
    </Typography>
  </Paper>
)

const Card = ({ record, onOpen, movable = true, ...props }) => {
  // records the member can't edit can still be opened, just not dragged
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: record.id, disabled: !movable })
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // a click (no drag movement) opens the record; dnd-kit only starts dragging after 5px
      onClick={() => onOpen(record)}
      aria-label={movable ? `${record.title}: drag to change stage, or press Enter to open` : `${record.title}: press Enter to open`}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(record); listeners?.onKeyDown?.(e) }}
      sx={{ opacity: isDragging ? 0.3 : 1, touchAction: movable ? 'none' : 'auto', '& > *': movable ? undefined : { cursor: 'pointer' }, outline: 'none', '&:focus-visible > *': { borderColor: 'primary.main' } }}
    >
      <CardBody record={record} {...props} />
    </Box>
  )
}

const Column = ({ column, sumField, children, onLoadMore, loadingMore }) => {
  const { setNodeRef, isOver } = useDroppable({ id: columnId(column.value) })
  const remaining = column.total - column.items.length
  return (
    <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 250px)' }}>
      <Box sx={{ px: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1, color: column.value ? 'text.primary' : 'text.secondary' }}>{column.label}</Typography>
          <Chip label={column.total} />
        </Box>
        {sumField && (
          <Typography variant="caption" color="text.secondary">{formatValue(sumField, column.sum ?? 0)}</Typography>
        )}
      </Box>
      <Box
        ref={setNodeRef}
        sx={{
          flexGrow: 1, overflowY: 'auto', p: 1, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 120,
          bgcolor: isOver ? 'action.selected' : 'action.hover', transition: 'background-color .15s',
        }}
      >
        {children}
        {column.items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Drop here</Typography>
        )}
        {remaining > 0 && (
          <Button onClick={onLoadMore} disabled={loadingMore}>{loadingMore ? 'Loading…' : `Load ${Math.min(remaining, PER_COLUMN)} more`}</Button>
        )}
      </Box>
    </Box>
  )
}

/**
 * Kanban view of a record type: one column per option of `groupBy`. Dragging a card PATCHes just
 * that field; the move shows immediately and is rolled back if the server refuses it.
 */
const RecordBoard = ({ entityType, state, byKey, cardFields, onOpen, canMove = () => true }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [dragging, setDragging] = useState(null)
  const [loadingMore, setLoadingMore] = useState(null)
  const [approvalAsk, setApprovalAsk] = useState(null)
  // releasing a dragged card also fires a click on it: don't open the record right after a drag
  const lastDragEnd = useRef(0)
  const open = (record) => { if (Date.now() - lastDragEnd.current > 300) onOpen(record) }
  const groupField = byKey[state.groupBy]
  const sumField = state.sumField ? byKey[state.sumField] : null

  const queryKey = [tenantId, 'records', entityType, 'board', { groupBy: state.groupBy, sumField: state.sumField, filters: state.filters, q: state.q, sort: state.sort }]
  const { data: board, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => recordsAPI.board({ entityType, groupBy: state.groupBy, sumField: state.sumField, perColumn: PER_COLUMN, sort: state.sort, q: state.q, filters: state.filters }),
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor))
  const findCard = (id) => {
    for (const column of board?.columns || []) {
      const record = column.items.find((r) => r.id === id)
      if (record) return { record, column }
    }
    return null
  }

  /** Moves a card between columns in the cached board (totals and sums included). */
  const moveInCache = (record, fromValue, toValue, replacement) => {
    const amount = sumField ? toNumber(record.data[sumField.fieldKey]) : 0
    queryClient.setQueryData(queryKey, (prev) => prev && {
      ...prev,
      columns: prev.columns.map((c) => {
        if (c.value === fromValue) {
          return { ...c, total: c.total - 1, sum: c.sum == null ? c.sum : toNumber(c.sum) - amount, items: c.items.filter((r) => r.id !== record.id) }
        }
        if (c.value === toValue) {
          return { ...c, total: c.total + 1, sum: c.sum == null ? c.sum : toNumber(c.sum) + amount, items: [replacement, ...c.items] }
        }
        return c
      }),
    })
  }

  const onDragEnd = async ({ active, over }) => {
    setDragging(null)
    lastDragEnd.current = Date.now()
    if (!over) return
    const found = findCard(String(active.id))
    if (!found) return
    const rawTarget = String(over.id).slice(4)
    const toValue = rawTarget === NONE ? null : rawTarget
    const fromValue = found.column.value
    if (toValue === fromValue) return

    const { record } = found
    const optimistic = { ...record, data: { ...record.data, [state.groupBy]: toValue ?? undefined } }
    moveInCache(record, fromValue, toValue, optimistic)
    try {
      const saved = await recordsAPI.patch(record.id, { [state.groupBy]: toValue }, record.lockVersion)
      // swap in the server's version (new lockVersion, updatedOn)
      queryClient.setQueryData(queryKey, (prev) => prev && {
        ...prev,
        columns: prev.columns.map((c) => (c.value === toValue ? { ...c, items: c.items.map((r) => (r.id === saved.id ? saved : r)) } : c)),
      })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records'], predicate: (q) => !(q.queryKey[3] === 'board') })
    } catch (e) {
      moveInCache(optimistic, toValue, fromValue, record)
      if (approvalNeeded(e)) {
        setApprovalAsk({ recordId: record.id, changes: { [state.groupBy]: toValue }, details: e.response.data.details })
        return
      }
      if (approvalPending(e)) {
        notify(getErrorMessage(e), 'warning')
        return
      }
      const detail = e?.response?.status === 409
        ? 'Someone else changed this record; the board was refreshed.'
        : [getErrorMessage(e), ...getErrorList(e)].join(' · ')
      notify(detail, 'error')
      if (e?.response?.status === 409) queryClient.invalidateQueries({ queryKey })
    }
  }

  const loadMore = async (column) => {
    setLoadingMore(column.value ?? NONE)
    try {
      const filters = { ...state.filters, [state.groupBy]: column.value ? { eq: column.value } : { empty: 'true' } }
      const page = await recordsAPI.list({ entityType, filters, q: state.q, sort: state.sort, size: PER_COLUMN, page: Math.floor(column.items.length / PER_COLUMN) })
      queryClient.setQueryData(queryKey, (prev) => prev && {
        ...prev,
        columns: prev.columns.map((c) => (c.value === column.value
          ? { ...c, items: [...c.items, ...page.items.filter((r) => !c.items.some((x) => x.id === r.id))] }
          : c)),
      })
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    } finally {
      setLoadingMore(null)
    }
  }

  if (!groupField) return <Alert severity="info">Choose a dropdown field to group the board by.</Alert>
  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)} {getErrorList(error).join('; ')}</Alert>

  const draggingCard = dragging ? findCard(dragging)?.record : null

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={({ active }) => setDragging(String(active.id))}
      onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
      <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
        {board.columns.map((column) => (
          <Column key={column.value ?? NONE} column={column} sumField={sumField}
            onLoadMore={() => loadMore(column)} loadingMore={loadingMore === (column.value ?? NONE)}>
            {column.items.map((record) => (
              <Card key={record.id} record={record} cardFields={cardFields} sumField={sumField} onOpen={open} movable={canMove(record)} />
            ))}
          </Column>
        ))}
      </Box>
      <DragOverlay dropAnimation={null}>
        {draggingCard && <Box sx={{ width: 264 }}><CardBody record={draggingCard} cardFields={cardFields} sumField={sumField} dragging /></Box>}
      </DragOverlay>
      {approvalAsk && <ApprovalAskDialog ask={approvalAsk} onClose={() => setApprovalAsk(null)} onAsked={() => queryClient.invalidateQueries({ queryKey })} />}
    </DndContext>
  )
}

export default RecordBoard
